// Safety register (Adverse events) — Blueprint §2, promoted into CARE for all
// roles because AE items bypass scope (F-SCOPE-03): patient safety is never
// "advanced". Ports legacy loadSafety/renderSafety (v20 lines ~1869-1892) onto
// data/ae.js. Lists v_safety_reconciliation with a reconciliation pill per row;
// a row opens the patient workspace overlay.
//
// SCOPE BYPASS is structural here: the register is programme-filtered only, never
// scope-filtered — a serious AE is visible to every nurse regardless of whose
// patient it is.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { register } from '../ui/actions.js';
import { registerView, countText } from './registry.js';
import { initials } from './shell.js';
import { progDotHTML } from '../programmes.js';
import { loadSafety, reconPill } from '../data/ae.js';
import { openWorkspace } from './workspace.js';

// ─── Pure filter logic (unit-tested) ────────────────────────────────────────

export const SAFETY_TABS = Object.freeze([
  Object.freeze({ id: 'all', label: 'All' }),
  Object.freeze({ id: 'serious', label: 'Serious' }),
  Object.freeze({ id: 'non_serious', label: 'Non-serious' }),
]);

/**
 * Rows visible under a filter tab (legacy renderSafety filter). Pure.
 * @param {any[]} rows
 * @param {string} tab 'all' | 'serious' | 'non_serious'
 * @returns {any[]}
 */
export function rowsForSafetyTab(rows, tab) {
  if (tab === 'serious') return (rows || []).filter((r) => r.seriousness === 'serious');
  if (tab === 'non_serious') return (rows || []).filter((r) => r.seriousness === 'non_serious');
  return rows || [];
}

/**
 * What the register may show for a SAFETY slice. PURE. An absent or unresolved
 * slice is 'loading' — never an empty register (review R-03: "No adverse
 * events" was painted from `s.rows || []` before loadSafety() had answered).
 * @param {any} slice SLICES.SAFETY value (may be undefined)
 * @returns {'loading'|'error'|'ready'}
 */
export function safetyListState(slice) {
  const s = slice || {};
  if (s.loadError) return 'error';
  if (!s.loaded) return 'loading';
  return 'ready';
}

// ─── Module state ──────────────────────────────────────────────────────────

let _tab = 'all';
let _wired = false;

/** Test-only. @returns {void} */
export function _resetSafety() {
  _tab = 'all';
  _wired = false;
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}
function safetyMounted() {
  const d = doc();
  const ui = store.get(SLICES.UI) || {};
  return !!d && ui.view === 'safety' && !!d.getElementById('safety-view');
}

function fmtDate(d) {
  if (!d) return '—';
  const t = new Date(d);
  if (isNaN(t.getTime())) return '—';
  return t.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function programmeHex(code) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const p = prog.byCode && prog.byCode[code];
  return p && p.brand_accent_hex;
}

// ─── Renderers ─────────────────────────────────────────────────────────────

function renderList() {
  const d = doc();
  const host = d && d.getElementById('safety-list');
  if (!host) return;
  const s = store.get(SLICES.SAFETY) || {};
  const state = safetyListState(s);

  if (state === 'loading') {
    host.innerHTML =
      '<div aria-busy="true" aria-label="Loading the safety register">' +
      '<div class="skel"></div><div class="skel"></div><div class="skel"></div></div>';
    return;
  }

  if (state === 'error') {
    host.innerHTML =
      '<button type="button" class="today-error" data-action="safety-retry">' +
      '<div><h2>Couldn’t load the safety register — tap to retry</h2>' +
      '<p>The query failed or was denied. Nothing is hidden — tap to try again.</p></div></button>';
    return;
  }

  const rows = rowsForSafetyTab(s.rows || [], _tab);
  if (!rows.length) {
    host.innerHTML =
      '<div class="today-empty"><h2>No adverse events</h2><p>Nothing to reconcile in this filter.</p></div>';
    return;
  }

  host.innerHTML =
    '<div class="safety-hdr"><span>Date</span><span>Reporter</span><span>Reaction</span><span>Severity</span><span>Reconciliation</span></div>' +
    rows.map((row) => safetyRowHTML(row)).join('');
}

function safetyRowHTML(row) {
  const recon = reconPill(row.reconciliation_status);
  const reporter = row.reporting_nurse || '—';
  const serious = row.seriousness === 'serious';
  const dot = row.programme_code ? progDotHTML(row.programme_code, programmeHex(row.programme_code), 8) : '';
  const caseId = row.case_id;
  const openable = !!caseId;
  return (
    `<div class="safety-row${serious ? ' serious' : ''}"${openable ? ' role="button" tabindex="0" data-kbd-click data-action="safety-open" data-case="' + esc(caseId) + '"' : ''} ` +
    `aria-label="${esc('Adverse event — ' + (row.reaction_term || 'reaction') + (openable ? ', open patient' : ''))}">` +
    `<span class="sf-date">${esc(fmtDate(row.report_date))}</span>` +
    '<span class="sf-rep">' +
    `<span class="sf-av" aria-hidden="true">${esc(initials(reporter))}</span>` +
    `<span class="sf-rep-n">${esc(reporter)}<span class="m-only"> · ${esc(row.reaction_term || '—')}${serious ? ' · SERIOUS' : ''}</span></span></span>` +
    `<span class="sf-react">${dot}<span>${esc(row.reaction_term || '—')}</span></span>` +
    `<span class="sf-sev">${serious ? '<span class="today-tag danger">SERIOUS</span>' : '<span class="today-tag">Non-serious</span>'}</span>` +
    `<span class="sf-recon"><span class="today-tag ${recon.cls}" title="${esc(recon.title)}">${esc(recon.label)}</span></span>` +
    '</div>'
  );
}

function renderTabs() {
  const d = doc();
  const host = d && d.getElementById('safety-tabs');
  if (!host) return;
  const s = store.get(SLICES.SAFETY) || {};
  const rows = s.rows || [];
  // A tab count of 0 reads as "no serious AEs", which is a clinical claim.
  // Only 'ready' has earned it: on 'error' the strip used to print "Serious 0"
  // directly above "Couldn't load the safety register".
  const state = safetyListState(s);
  const count = (id) => countText(state, rowsForSafetyTab(rows, id).length);
  host.innerHTML = SAFETY_TABS.map((t) => {
    const active = _tab === t.id;
    return (
      `<button type="button" class="today-tab${active ? ' active' : ''}" data-action="safety-tab" data-tab="${esc(t.id)}" aria-pressed="${active}">` +
      `${esc(t.label)}<span class="tb-n">${esc(count(t.id))}</span></button>`
    );
  }).join('');
}

function renderBody() {
  if (!safetyMounted()) return;
  renderTabs();
  renderList();
}

// ─── Actions ────────────────────────────────────────────────────────────────

async function refresh() {
  const res = await loadSafety(); // returned so Sync can see a failed refresh
  renderBody();
  return res;
}

function onTab(el) {
  const tab = el.getAttribute('data-tab');
  if (!tab || !SAFETY_TABS.some((t) => t.id === tab)) return;
  _tab = tab;
  renderBody();
}

function onOpen(el) {
  const caseId = el.getAttribute('data-case');
  if (caseId) void openWorkspace(caseId);
}

function wireOnce() {
  if (_wired) return;
  _wired = true;
  register('safety-tab', onTab);
  register('safety-retry', () => void refresh());
  register('safety-open', onOpen);
  store.subscribe(SLICES.SAFETY, () => renderBody());
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (safetyMounted() && v && Array.isArray(v.changed) && v.changed.includes('safety')) void refresh();
  });
}

function skeleton() {
  return (
    '<div id="safety-view" class="safety">' +
    '<header class="view-head"><h1>Adverse events</h1>' +
    '<p class="safety-sub">Every reported event and its pharmacovigilance reconciliation status. Safety items are shown to everyone — never hidden by scope.</p></header>' +
    '<div id="safety-tabs" class="today-tabs" role="group" aria-label="Filter adverse events"></div>' +
    '<div id="safety-list" class="safety-list today-card"></div>' +
    '</div>'
  );
}

/**
 * Register the Safety view, replacing the Phase-1 placeholder. Call once at boot.
 * @returns {void}
 */
export function registerSafety() {
  registerView('safety', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      renderBody();
    },
    load() {
      return refresh();
    },
  });
}
